import { clsx } from "clsx";
import { useParcelStatusMatrix } from "../hooks";
import type { StatusMatrixCell, StatusMatrixTone } from "../types";

// Tabela statusów działki w „Danych ogólnych": jedna kolumna na maszynę stanów
// (tytuły właścicieli, sprawa, wniosek w sprawie, pakiet WNU, wypis, MZ,
// MDCP, zlecenie WPW, deklaracja negocjatora), w każdej bieżący status powiązanego
// obiektu albo „brak". Kolejność i etykiety przychodzą z backendu — front tylko
// koloruje po `tone` i oznacza komórki wspólne dla kilku działek.
//
// Kolory tonów = paleta chipów zamówień (ORDER_STATUS_COLOR w ParcelsTab), żeby te
// same stany czytały się tak samo w całej zakładce działek.
const TONE_COLOR: Record<StatusMatrixTone, string | undefined> = {
  none: undefined,
  neutral: undefined,
  progress: "#2563eb",
  waiting: "#d97706",
  field: "#7c3aed",
  ok: "#059669",
  bad: "#dc2626",
};

// Wspólne tło grupy kolumn (sprawa + wnioski + zlecenie WPW — tylko to, co wpływa na
// podstawę dostępu; deklaracja negocjatora poza grupą): neutralny, stalowoszary podkład,
// którego nie ma nigdzie indziej w tym widoku (celowo NIE kolor podstawowy ani żaden
// z kolorów statusów); półprzezroczysty, więc czytelny w obu motywach. Nagłówek mocniejszy.
const GROUP_CELL_STYLE = { background: "rgb(100 116 139 / 0.10)" } as const;
const GROUP_HEAD_STYLE = { background: "rgb(100 116 139 / 0.20)" } as const;

const SHARED_TITLE =
  "Obiekt zbiorczy — obejmuje więcej działek niż ta jedna, więc ten sam status widać na każdej z nich";

export function StatusMatrixCellView({ cell }: { cell: StatusMatrixCell }) {
  const empty = cell.status === null;
  const color = TONE_COLOR[cell.tone];
  return (
    <div className="flex flex-col gap-0.5 min-w-[7.5rem]">
      <div className="flex items-start gap-1">
        <span
          className={clsx(
            "text-[11px] leading-tight",
            empty ? "italic text-[var(--color-text-muted)]" : cell.emphasis ? "font-bold" : "font-medium",
            !empty && !color && "text-[var(--color-text-secondary)]",
          )}
          style={color ? { color } : undefined}
        >
          {cell.status_display}
        </span>
        {cell.shared && (
          <span
            className="shrink-0 text-[9px] font-mono uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)] leading-tight"
            title={SHARED_TITLE}
          >
            wspólny
          </span>
        )}
      </div>
      {cell.detail && (
        <span className="text-[10px] leading-tight text-[var(--color-text-muted)] break-words">{cell.detail}</span>
      )}
    </div>
  );
}

export function ParcelStatusMatrixTable({ parcelId }: { parcelId: string }) {
  const { data, isLoading, isError } = useParcelStatusMatrix(parcelId);
  if (isLoading) {
    return <div className="text-[11px] text-[var(--color-text-muted)] px-3 py-2">Ładowanie statusów…</div>;
  }
  if (isError || !data) {
    return <div className="text-[11px] text-[var(--color-danger)] px-3 py-2">Nie udało się pobrać statusów działki.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse" data-testid="parcel-status-matrix">
        <thead>
          <tr>
            {data.columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={clsx(
                  "text-left align-bottom px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.08em] whitespace-nowrap border-b border-[var(--color-border)]",
                  c.emphasis ? "font-extrabold text-[var(--color-text-primary)]" : "font-bold text-[var(--color-text-muted)]",
                  c.group ? "" : "bg-[var(--color-bg-elevated)]",
                )}
                style={c.group ? GROUP_HEAD_STYLE : undefined}
                data-group={c.group ?? undefined}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {data.columns.map((c) => (
              <td
                key={c.key}
                className="align-top px-3 py-2 border-r last:border-r-0 border-[var(--color-border-muted)]"
                style={c.group ? GROUP_CELL_STYLE : undefined}
                data-group={c.group ?? undefined}
              >
                <StatusMatrixCellView cell={c} />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
